/**
 * Brand inheritance — derives a stable, opinionated palette for a venture
 * from the linked build (project_id) and venture name. No LLM call, no
 * runtime CSS extraction yet — this is the V1 "feels brand-native" layer.
 *
 * The palette is intentionally CALM (not casino-bright) — accents stay
 * subtle so animated data on top of them never looks like a fake dashboard.
 *
 * Used by TractionBubble (chip preview) and TractionPreview (zone accents).
 */

const PALETTES = [
  // Linear-Stripe baseline (default dark)
  {
    key: 'linear',
    bg: '#0A0A0B',
    surface: '#13131A',
    surface2: '#1B1B26',
    border: 'rgba(255,255,255,0.08)',
    text: '#F5F5F7',
    textMute: 'rgba(245,245,247,0.62)',
    textDim: 'rgba(245,245,247,0.38)',
    accent: '#5EEAD4',         // mint
    accentSoft: 'rgba(94,234,212,0.16)',
    accent2: '#A78BFA',        // soft violet
    positive: '#34D399',
    warn: '#FBBF24',
    danger: '#F87171',
    grain: 0.02,
  },
  // Premium / luxe (noir-or)
  {
    key: 'luxe',
    bg: '#0B0907',
    surface: '#151210',
    surface2: '#1F1A15',
    border: 'rgba(255,215,160,0.10)',
    text: '#F8F1E0',
    textMute: 'rgba(248,241,224,0.62)',
    textDim: 'rgba(248,241,224,0.36)',
    accent: '#E8C870',         // gold (toned)
    accentSoft: 'rgba(232,200,112,0.14)',
    accent2: '#C9A059',
    positive: '#A3D9A5',
    warn: '#E8C870',
    danger: '#E58680',
    grain: 0.03,
  },
  // Creator / Gen Z (subtle gradient violet→pink)
  {
    key: 'creator',
    bg: '#0E0A14',
    surface: '#17121F',
    surface2: '#211829',
    border: 'rgba(236,72,153,0.12)',
    text: '#F5EFFA',
    textMute: 'rgba(245,239,250,0.62)',
    textDim: 'rgba(245,239,250,0.38)',
    accent: '#F472B6',         // pink
    accentSoft: 'rgba(244,114,182,0.14)',
    accent2: '#A78BFA',
    positive: '#86EFAC',
    warn: '#FBBF24',
    danger: '#F87171',
    grain: 0.025,
  },
  // B2B / Stripe-like (cool blue)
  {
    key: 'b2b',
    bg: '#09090F',
    surface: '#12141B',
    surface2: '#191C25',
    border: 'rgba(96,165,250,0.10)',
    text: '#EEF2FF',
    textMute: 'rgba(238,242,255,0.62)',
    textDim: 'rgba(238,242,255,0.38)',
    accent: '#60A5FA',         // cool blue
    accentSoft: 'rgba(96,165,250,0.14)',
    accent2: '#7DD3FC',
    positive: '#34D399',
    warn: '#FBBF24',
    danger: '#F87171',
    grain: 0.02,
  },
  // Indie / minimal (warm beige on near-black)
  {
    key: 'indie',
    bg: '#0C0C0A',
    surface: '#15140F',
    surface2: '#1D1B16',
    border: 'rgba(229,217,194,0.10)',
    text: '#F1EBD9',
    textMute: 'rgba(241,235,217,0.62)',
    textDim: 'rgba(241,235,217,0.38)',
    accent: '#E5D9C2',
    accentSoft: 'rgba(229,217,194,0.12)',
    accent2: '#C9B891',
    positive: '#A3D9A5',
    warn: '#E8C870',
    danger: '#E58680',
    grain: 0.025,
  },
];

const KEYWORD_RULES = [
  { match: /(luxe|luxur|premium|haute|fashion|jewel|elite|private)/i, palette: 'luxe' },
  { match: /(creator|tiktok|insta|video|content|stream|gen.?z|viral)/i, palette: 'creator' },
  { match: /(crm|saas|b2b|enterprise|hr|payroll|finance|fintech|account|cloud|api)/i, palette: 'b2b' },
  { match: /(indie|solo|maker|caf[eé]|brand|atelier|studio|craft|local)/i, palette: 'indie' },
];

/**
 * Deterministic FNV-1a hash → 0..N-1
 */
function hashIndex(str, modulo) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 0x01000193) >>> 0;
  }
  return h % modulo;
}

/**
 * @param {{ name?: string, project_id?: string, monetize_id?: string, project?: object }} venture
 * @returns palette object (full)
 */
export function paletteForVenture(venture) {
  const text = [
    venture?.name,
    venture?.project_id,
    venture?.project?.title,
    venture?.project?.subtitle,
    venture?.monetize_id,
  ].filter(Boolean).join(' ');

  // 1. keyword match wins
  for (const rule of KEYWORD_RULES) {
    if (rule.match.test(text)) {
      return PALETTES.find(p => p.key === rule.palette) || PALETTES[0];
    }
  }
  // 2. otherwise stable hash → palette
  if (text) {
    const idx = hashIndex(text, PALETTES.length);
    return PALETTES[idx];
  }
  // 3. fallback
  return PALETTES[0];
}

export const DEFAULT_PALETTE = PALETTES[0];
export { PALETTES };
